import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteConfigAPI, stripeAPI, authAPI } from '../../services/api';
import { SubscriptionPlan } from '../../types';

export const usePricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);
  const [checkoutError, setCheckoutError] = useState<string>('');
  const [currentPlanSlug, setCurrentPlanSlug] = useState<string | null>(null);

  const getLoggedInUser = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.is_company || user.role === 'company') return user;
      }
    } catch {}
    return null;
  };

  const loggedInUser = getLoggedInUser();

  useEffect(() => {
    const fetches: Promise<void>[] = [
      siteConfigAPI.getPlans().then(res => setPlans(res.data || [])),
      siteConfigAPI.getPublicConfig().then(res => setHighlights(res.data?.pricing_highlights || [])),
    ];

    // Fetch current plan from database for logged-in company users
    if (loggedInUser && localStorage.getItem('token')) {
      fetches.push(
        authAPI.getProfile().then(res => {
          const slug = res.data?.plan?.slug;
          setCurrentPlanSlug(slug || 'free');
        }).catch(() => {
          // Fallback to localStorage if profile fetch fails
          setCurrentPlanSlug(loggedInUser?.plan?.slug || 'free');
        })
      );
    }

    Promise.all(fetches)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isFree = (price: string | number) => Number(price) === 0;

  const getPlanColor = (slug: string) => {
    switch (slug) {
      case 'free': return { bg: '#ecfdf5', border: '#a7f3d0', accent: '#059669', btn: 'linear-gradient(135deg, #059669, #10b981)', btnHover: '#047857', text: '#065f46' };
      case 'starter': return { bg: '#eff6ff', border: '#bfdbfe', accent: '#2563eb', btn: 'linear-gradient(135deg, #2563eb, #3b82f6)', btnHover: '#1d4ed8', text: '#1e40af' };
      case 'growth': return { bg: '#f5f3ff', border: '#c4b5fd', accent: '#7c3aed', btn: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', btnHover: '#6d28d9', text: '#5b21b6' };
      case 'pro': return { bg: '#fff7ed', border: '#fed7aa', accent: '#ea580c', btn: 'linear-gradient(135deg, #ea580c, #f97316)', btnHover: '#c2410c', text: '#9a3412' };
      default: return { bg: '#f8fafc', border: '#cbd5e1', accent: '#475569', btn: 'linear-gradient(135deg, #475569, #64748b)', btnHover: '#334155', text: '#334155' };
    }
  };

  const getLimitLabel = (plan: SubscriptionPlan) => {
    if (plan.customer_limit === 0) return 'Unlimited wholesale clients';
    return `Up to ${plan.customer_limit} wholesale clients`;
  };

  const formatPrice = (price: string | number) => {
    const n = Number(price);
    return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
  };

  const handlePlanClick = async (plan: SubscriptionPlan) => {
    const free = isFree(plan.price);

    // Free plans: go to register
    if (free) {
      const url = plan.cta_url || '/register';
      url.startsWith('http') ? window.open(url, '_blank') : navigate(url);
      return;
    }

    // Paid plan: if logged in as company, create checkout session
    if (loggedInUser) {
      setCheckoutLoading(plan.id);
      try {
        const res = await stripeAPI.createCheckoutSession(plan.id);
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || 'Failed to start checkout. Please try again.';
        setCheckoutError(msg);
        setCheckoutLoading(null);
      }
    } else {
      // Not logged in: go to register
      navigate('/register');
    }
  };

  const navigateToRegister = () => navigate('/register');

  const freePlan = plans.find(p => isFree(p.price));
  const paidPlans = plans.filter(p => !isFree(p.price));

  return {
    highlights,
    loading,
    checkoutLoading,
    checkoutError,
    setCheckoutError,
    loggedInUser,
    currentPlanSlug,
    freePlan,
    paidPlans,
    isFree,
    getPlanColor,
    getLimitLabel,
    formatPrice,
    handlePlanClick,
    navigateToRegister,
  };
};
